// api/utils.js
// Date parsing utilities
export function parseDate(dateString) {
    if (!dateString) return 'N/A';
    
    // Handle various date formats
    let date;
    
    // Try ISO format first (YYYY-MM-DD)
    if (typeof dateString === 'string' && dateString.includes('-')) {
      date = new Date(dateString);
    } 
    // Try SAHPRA format (YYYY/MM/DD)
    else if (typeof dateString === 'string' && dateString.includes('/')) {
      const [year, month, day] = dateString.split('/');
      date = new Date(year, month - 1, day);
    }
    // Try Date object or timestamp
    else {
      date = new Date(dateString);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    // Format date as DD MMM YYYY (e.g., 15 Jun 2023)
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
  }
  
  // Format a price value with currency symbol
  export function formatPrice(price) {
    if (!price) return 'N/A';
    
    // Check if it's already formatted
    if (typeof price === 'string' && price.includes('R')) {
      return price;
    }
    
    // Convert to number and format
    const numValue = parseFloat(price);
    if (isNaN(numValue)) return 'N/A';
    
    return `R ${numValue.toFixed(2)}`;
  }
  
  // Extract schedule information from text
  export function extractSchedule(text) {
    if (!text) return null;
    
    const scheduleMatch = /schedule\s+([0-9]+)/i.exec(text);
    if (scheduleMatch) {
      return `Schedule ${scheduleMatch[1]}`;
    }
    
    return null;
  }
  
  // Clean and standardize medicine names
  export function cleanMedicineName(name) {
    if (!name) return '';
    
    // Convert to uppercase
    let cleanName = name.toUpperCase();
    
    // Remove dosage information (e.g., "30MG")
    cleanName = cleanName.replace(/\s+\d+(?:\.\d+)?\s*(?:MG|ML|MCG|G)\b/i, '');
    
    // Remove parenthetical information
    cleanName = cleanName.replace(/\s*\([^)]*\)/g, '');
    
    // Trim whitespace and return
    return cleanName.trim();
  }
  
  // Extract dosage information in a standardized format
  export function extractDosage(text) {
    if (!text) return null;
    
    const dosageMatch = /(\d+(?:[.,]\d+)?\s*(?:mg|ml|mcg|g))/i.exec(text);
    if (dosageMatch) {
      // Standardize format (e.g., "30mg")
      return dosageMatch[1].replace(',', '.').replace(/\s+/g, '').toLowerCase();
    }
    
    return null;
  }
  
  // Generate a unique ID for cache/search purposes
  export function generateSearchId(query, includeDate = false) {
    const baseId = query.toUpperCase().trim();
    
    if (includeDate) {
      return `${baseId}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;
    }
    
    return baseId;
  }
  
  // Check if a text contains schedule information
  export function isControlledSubstance(item) {
    if (!item) return false;
    
    // Check various fields that might contain schedule information
    return Boolean(
      item.schedule || 
      (item.ingredient && item.ingredient.toLowerCase().includes('schedule')) ||
      (item.therapeutic_area && item.therapeutic_area.toLowerCase().includes('schedule'))
    );
  }
  
  // Get the schedule information from an item
  export function getScheduleInfo(item) {
    if (!item) return null;
    
    // Check explicit schedule field first
    if (item.schedule) {
      return item.schedule;
    }
    
    // Try to extract from ingredient
    if (item.ingredient && item.ingredient.toLowerCase().includes('schedule')) {
      const match = /schedule\s+([0-9]+)/i.exec(item.ingredient);
      return match ? `Schedule ${match[1]}` : 'Controlled';
    }
    
    // Try therapeutic area
    if (item.therapeutic_area && item.therapeutic_area.toLowerCase().includes('schedule')) {
      const match = /schedule\s+([0-9]+)/i.exec(item.therapeutic_area);
      return match ? `Schedule ${match[1]}` : 'Controlled';
    }
    
    return null;
  }
  
  // Helper to safely get a value from an object with fallbacks
  export function getFieldValue(item, fieldNames, defaultValue = 'N/A') {
    if (!item) return defaultValue;
    
    for (const field of fieldNames) {
      if (item[field] !== undefined && item[field] !== null && item[field] !== '') {
        return item[field];
      }
    }
    
    return defaultValue;
  }
  
  /**
   * Formats a date value for display
   * 
   * @param {string|Date} dateVal - Date value to format
   * @return {string} - Formatted date string
   */
  export function formatDate(dateVal) {
    if (!dateVal) return 'N/A';
    
    try {
      if (typeof dateVal === 'string') {
        // Handle ISO strings or other string formats
        const date = new Date(dateVal);
        if (isNaN(date.getTime())) return dateVal; // Return original if invalid
        return date.toLocaleDateString();
      } else if (dateVal instanceof Date) {
        return dateVal.toLocaleDateString();
      }
      return 'N/A';
    } catch (e) {
      console.error('Date formatting error:', e);
      return 'N/A';
    }
  }